import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Bot,
    ChevronDown,
    ExternalLink,
    Loader2,
    Mic,
    MessageSquare,
    Send,
    Sparkles,
    Sunrise,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { AiMarkdown } from "./AiMarkdown";
import AkilOnayPanel from "./AkilOnayPanel";
import { akilChat } from "../../lib/akilApi";
import {
    AKIL_WIDGET_CLOSE_EVENT,
    AKIL_WIDGET_OPEN_EVENT,
    AKIL_WIDGET_PREF_EVENT,
    closeAkilWidget,
    isAkilWidgetAutoOpen,
    isAkilWidgetEnabled,
    openAkilWidget,
} from "../../lib/akilWidgetPrefs";
import {
    AKIL_CHAT_GREETING,
    AKIL_WIDGET_QUICK_PROMPTS,
    formatAkilReply,
} from "../../lib/akilChatUtils";
import { buildContextPrompt, getAkilContext } from "../../lib/akilContext";
import { useAkilSpeech } from "../../lib/useAkilSpeech";
import AI_SIDEBAR from "../../lib/aiSidebar";

export { openAkilWidget, closeAkilWidget } from "../../lib/akilWidgetPrefs";

export default function AkilFloatingWidget({ onOpenChange }) {
    const location = useLocation();
    const [enabled, setEnabled] = useState(isAkilWidgetEnabled);
    const [open, setOpen] = useState(false);
    const [context, setContext] = useState(null);
    const [messages, setMessages] = useState([{ role: "ai", text: AKIL_CHAT_GREETING }]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState(null);
    const [useContext, setUseContext] = useState(true);
    const scrollRef = useRef(null);

    const hidden =
        location.pathname === "/login" || location.pathname.startsWith("/ai/sohbet");

    const setOpenSafe = useCallback(
        (v) => {
            setOpen(v);
            onOpenChange?.(v);
        },
        [onOpenChange],
    );

    useEffect(() => {
        const sync = () => setEnabled(isAkilWidgetEnabled());
        sync();
        window.addEventListener(AKIL_WIDGET_PREF_EVENT, sync);
        return () => window.removeEventListener(AKIL_WIDGET_PREF_EVENT, sync);
    }, []);

    useEffect(() => {
        const onOpen = () => setOpenSafe(true);
        const onClose = () => setOpenSafe(false);
        window.addEventListener(AKIL_WIDGET_OPEN_EVENT, onOpen);
        window.addEventListener(AKIL_WIDGET_CLOSE_EVENT, onClose);
        return () => {
            window.removeEventListener(AKIL_WIDGET_OPEN_EVENT, onOpen);
            window.removeEventListener(AKIL_WIDGET_CLOSE_EVENT, onClose);
        };
    }, [setOpenSafe]);

    useEffect(() => {
        if (location.pathname === "/ai/merkez" && isAkilWidgetAutoOpen() && isAkilWidgetEnabled()) {
            setOpenSafe(true);
        }
    }, [location.pathname, setOpenSafe]);

    useEffect(() => {
        if (open) {
            setContext(getAkilContext());
            setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 80);
        }
    }, [open, messages, loading, location.pathname]);

    const { listening, start: startSpeech } = useAkilSpeech((text) => {
        setInput((prev) => (prev ? `${prev} ${text}` : text));
    });

    const send = useCallback(
        async (text, opts = {}) => {
            const raw = (text || input).trim();
            if (!raw && !opts.parsed) return;
            const ctxPrompt = useContext ? buildContextPrompt(context) : "";
            const mesaj = opts.skipContext ? raw : ctxPrompt + raw;
            setLoading(true);
            if (raw && !opts.silentUser) {
                setMessages((m) => [...m, { role: "user", text: raw }]);
                setInput("");
            }
            try {
                const payload = opts.parsed
                    ? {
                          parsed: opts.parsed,
                          onay: !!opts.onay,
                          mesaj: mesaj || "onay",
                          context: context || getAkilContext(),
                      }
                    : { mesaj, context: context || getAkilContext() };
                const data = await akilChat(payload);

                if (data.onay_gerekli && !opts.onay) {
                    setPending({
                        parsed: data.parsed,
                        aciklama: data.aciklama || data.mesaj,
                        onay_ozeti: data.onay_ozeti,
                    });
                    setMessages((m) => [
                        ...m,
                        {
                            role: "ai",
                            text: data.onay_ozeti || data.aciklama || data.mesaj || "Onay gerekli.",
                        },
                    ]);
                    return;
                }

                setPending(null);
                setMessages((m) => [...m, { role: "ai", text: formatAkilReply(data) }]);
            } catch (err) {
                const msg =
                    err.response?.data?.error ||
                    err.response?.data?.detail ||
                    (err.code === "ECONNABORTED" ? "Zaman aşımı — tekrar deneyin." : err.message);
                toast.error(String(msg));
                setMessages((m) => [...m, { role: "ai", text: `Hata: ${msg}` }]);
            } finally {
                setLoading(false);
            }
        },
        [input, context, useContext],
    );

    if (hidden || !enabled) return null;

    return (
        <div className="fixed bottom-4 right-4 z-[90] flex flex-col items-end gap-3 pointer-events-none">
            {open ? (
                <div
                    className="pointer-events-auto w-[min(100vw-2rem,400px)] h-[min(72vh,560px)] rounded-2xl border border-[#D4AF37]/35 bg-[#0B0F19]/95 backdrop-blur-xl shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
                    role="dialog"
                    aria-label="MBS Hukuk Akıl asistanı"
                >
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#D4AF37]/15 to-transparent shrink-0">
                        <span className="h-9 w-9 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center">
                            <Sparkles className="h-4 w-4 text-[#F3E5AB]" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]">
                                MBS Hukuk
                            </p>
                            <p className="text-sm font-semibold text-white truncate">Akıl · Yapay Zeka</p>
                            {context?.muvekkil_label || context?.dosya_no ? (
                                <p className="text-[10px] text-slate-400 truncate mt-0.5">
                                    {buildContextPrompt(context).replace(/\.\s*$/, "")}
                                </p>
                            ) : null}
                        </div>
                        <Link
                            to="/ai/merkez"
                            className="hidden sm:flex h-8 px-2 rounded-lg border border-white/10 items-center text-[10px] text-slate-400 hover:text-[#D4AF37]"
                            title="Akıl Merkez"
                        >
                            Merkez
                        </Link>
                        <Link
                            to="/ai/sohbet"
                            className="h-8 w-8 rounded-lg border border-white/10 flex items-center justify-center text-slate-400 hover:text-[#D4AF37] hover:border-[#D4AF37]/40"
                            title="Tam ekran sohbet"
                        >
                            <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                        <button
                            type="button"
                            onClick={() => setOpenSafe(false)}
                            className="h-8 w-8 rounded-lg border border-white/10 flex items-center justify-center text-slate-400 hover:text-white"
                            aria-label="Kapat"
                        >
                            <ChevronDown className="h-4 w-4" />
                        </button>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
                        {messages.map((msg, i) => (
                            <div
                                key={i}
                                className={`max-w-[95%] rounded-xl px-3 py-2 text-sm ${
                                    msg.role === "user"
                                        ? "ml-auto bg-[#D4AF37]/15 border border-[#D4AF37]/25 text-slate-100"
                                        : "mr-auto bg-white/[0.04] border border-white/10 text-slate-200"
                                }`}
                            >
                                {typeof msg.text === "string" ? (
                                    <AiMarkdown text={msg.text} />
                                ) : (
                                    msg.text
                                )}
                            </div>
                        ))}
                        {loading ? (
                            <div className="flex items-center gap-2 text-slate-500 text-xs px-1">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Akıl düşünüyor…
                            </div>
                        ) : null}
                    </div>

                    {pending ? (
                        <div className="mx-3 mb-2 shrink-0">
                            <AkilOnayPanel
                                ozet={pending.onay_ozeti || pending.aciklama}
                                onConfirm={() =>
                                    send("", {
                                        parsed: pending.parsed,
                                        onay: true,
                                        silentUser: true,
                                        skipContext: true,
                                    })
                                }
                                onCancel={() => setPending(null)}
                                loading={loading}
                            />
                        </div>
                    ) : null}

                    <div className="border-t border-white/10 p-3 shrink-0 space-y-2 bg-[#121A2F]/80">
                        <div className="flex flex-wrap gap-1">
                            {AKIL_WIDGET_QUICK_PROMPTS.map((q) => (
                                <button
                                    key={q}
                                    type="button"
                                    onClick={() => send(q)}
                                    disabled={loading}
                                    className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-slate-500 hover:text-white hover:border-[#D4AF37]/40 disabled:opacity-40"
                                >
                                    {q}
                                </button>
                            ))}
                        </div>
                        <form
                            className="flex gap-2"
                            onSubmit={(e) => {
                                e.preventDefault();
                                send();
                            }}
                        >
                            <button
                                type="button"
                                onClick={startSpeech}
                                disabled={loading || listening}
                                className={`h-9 w-9 rounded-lg border flex items-center justify-center shrink-0 ${
                                    listening
                                        ? "border-red-400/50 text-red-300 animate-pulse"
                                        : "border-white/10 text-slate-400 hover:text-[#D4AF37]"
                                }`}
                                title="Sesli komut / dikte"
                            >
                                <Mic className="h-4 w-4" />
                            </button>
                            <input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={
                                    context ? "Bu dosya hakkında sorun…" : "Sorunuzu yazın…"
                                }
                                className="flex-1 rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-xs outline-none focus:border-[#D4AF37]/50"
                            />
                            <button
                                type="submit"
                                disabled={loading || !input.trim()}
                                className="h-9 w-9 rounded-lg bg-[#D4AF37] text-black flex items-center justify-center disabled:opacity-40 shrink-0"
                            >
                                <Send className="h-4 w-4" />
                            </button>
                        </form>
                        <div className="flex flex-wrap gap-1 pt-1">
                            {AI_SIDEBAR.items.slice(0, 4).map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className="text-[10px] px-2 py-0.5 rounded border border-white/5 text-slate-500 hover:text-[#D4AF37]"
                                >
                                    {item.title}
                                </Link>
                            ))}
                            <Link
                                to={
                                    context?.sigorta_dosyasi_id
                                        ? `/ai/mesaj-taslagi?dosya_id=${context.sigorta_dosyasi_id}&sablon=eksik_evrak`
                                        : "/ai/mesaj-taslagi"
                                }
                                className="text-[10px] px-2 py-0.5 rounded border border-white/5 text-slate-500 hover:text-[#D4AF37] inline-flex items-center gap-1"
                            >
                                <MessageSquare className="h-3 w-3" /> Mesaj
                            </Link>
                            <Link
                                to="/ai/gunluk-brifing"
                                className="text-[10px] px-2 py-0.5 rounded border border-white/5 text-slate-500 hover:text-[#D4AF37] inline-flex items-center gap-1"
                            >
                                <Sunrise className="h-3 w-3" /> Brifing
                            </Link>
                        </div>
                    </div>
                </div>
            ) : null}

            <button
                type="button"
                onClick={() => setOpenSafe((v) => !v)}
                className="pointer-events-auto group relative flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B8960C] text-slate-900 shadow-lg shadow-[#D4AF37]/25 hover:shadow-[#D4AF37]/40 transition-all hover:scale-[1.02]"
                aria-expanded={open}
                aria-label={open ? "Akıl widgetını kapat" : "MBS Hukuk Akıl yapay zeka"}
            >
                {!open ? (
                    <span className="absolute inset-0 rounded-full bg-[#D4AF37]/30 animate-ping opacity-30 group-hover:opacity-50" />
                ) : null}
                <span className="relative h-9 w-9 rounded-full bg-black/15 flex items-center justify-center">
                    {open ? <X className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
                </span>
                <span className="relative text-sm font-bold hidden sm:inline">
                    {open ? "Kapat" : "Akıl"}
                </span>
            </button>
        </div>
    );
}
